import moment from 'moment-timezone';
import React from 'react';

import DateTime from 'core/definitions/DateTime';
import { L10nConsumer, LocalizedMessage } from 'core/l10n/components';
import * as OvationsApi from 'core/ovations-api';
import { questionFieldIsDisabled, questionFieldIsRequired } from 'core/ovations-components/QuestionField';
import { DateTimePicker } from 'core/ovations-components/DateTimePicker';

import QuestionFieldProps from './definitions/QuestionFieldProps';

export type DateTimeQuestionProps = QuestionFieldProps<
    OvationsApi.Types.PromotionDateTimeQuestion,
    OvationsApi.Types.PromotionDateTimeQuestionAnswer
>;

const getMaxAllowableDate = (promotionQuestion: OvationsApi.Types.PromotionDateTimeQuestion): DateTime => {
    if (promotionQuestion.allowFutureDates) {
        return promotionQuestion.endDate;
    }

    const localEndOfDay = moment().endOf('day');
    return moment.min(localEndOfDay, moment(promotionQuestion.endDate)).toISOString();
};

const getMinAllowableDate = (props: DateTimeQuestionProps): DateTime | null => {
    const { isCallCenter, promotionQuestion } = props;
    if (isCallCenter) {
        return null;
    }

    return moment(promotionQuestion.startDate).toISOString();
};

const getValidationProps = (props: DateTimeQuestionProps) => {
    const { promotionQuestion } = props;
    return {
        minDate: getMinAllowableDate(props),
        maxDate: getMaxAllowableDate(promotionQuestion),
    };
};

const DateTimeQuestionField: React.SFC<DateTimeQuestionProps> = (props) => {
    const validationProps = getValidationProps(props);
    return (
        <L10nConsumer>
            {(l10n) => {
                const dateFormat = l10n.localizeDateFormat();
                const friendlyMin = moment(getMinAllowableDate(props)).format(dateFormat);
                const friendlyMax = moment(getMaxAllowableDate(props.promotionQuestion)).format(dateFormat);
                return (
                    <DateTimePicker
                        id={`question_${props.promotionQuestion.id}`}
                        disabled={questionFieldIsDisabled(props)}
                        required={questionFieldIsRequired(props)}
                        selectedDate={props.answer ? props.answer.value : ''}
                        onChange={(dateTime: string | null) => {
                            if (!props.onChange) {
                                return;
                            }
                            props.onChange({
                                type: OvationsApi.Enums.QuestionType.DateTime,
                                value: dateTime,
                            });
                        }}
                        {...validationProps}
                        invalidFeedback={
                            <LocalizedMessage
                                id="questionForm_errorMessage_invalidDate"
                                values={{ minDate: friendlyMin, maxDate: friendlyMax }}
                            />
                        }
                        dateFormat={dateFormat}
                        placeholderText={l10n.localizeMessage('generic_dateInput_placeholder')}
                        popperPlacement="bottom"
                    />
                );
            }}
        </L10nConsumer>
    );
};

export default DateTimeQuestionField;
